import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AccountConfigComponent } from './account/account-config/account-config.component';
import { ActiveAccountsComponent } from './active-accounts/active-accounts.component';
import { Auth0LoginComponent } from './auth/auth0-login/auth0-login.component';
import { ClientDetailsComponent } from './clients/client-details/client-details.component';
import { SignatureCallbackComponent } from './components/signature-envelope-dialog/signature-callback.component';
import { TrustCommerceCallbackComponent } from './components/trustcommerce/trustcommerce-callback.component';
import { ApplicantEndOfInterviewComponent } from './interview/end-of-interview/applicant-end-of-interview.component';
import { InterviewComponent } from './interview/interview.component';
import { MyCasesComponent } from './my-cases/my-cases.component';
import { NavigationComponent } from './navigation/navigation.component';
import CaseManagerRoute from './new-business-workbench/routing/casemanager.route';
import { NotFoundComponent } from './not-found.component';
import * as Guards from './services/guards';
import * as Resolvers from './services/resolvers';
import { CanNavigateAwayWithConfirmGuardAccountConfig } from "app/new-business-workbench/guards/canNavigateAway.guard";
import { CaseAssignmentAndRestartComponent } from './components/case-assignment/case-assignment-and-restart.component';
import { AuditRoutes } from '@Pages';
import { InterviewBootstrapComponent } from '@Components/mras/interview';
import { RoleBuilderComponent } from './roles/role-builder.component';
import { RoutesEnum } from '@Services';
import { AppTeamsComponent } from './manage-users/components/teams/app-teams.component';
import { TeamOverviewComponent } from './manage-users/components/team-overview/team-overview.component';
import { MyAccountComponent } from './manage-users/components/my-account/my-account.component';
import { MyDashboardComponent } from './my-dashboard/my-dashboard.component';
import ViewDocumentPage from '@Pages/view-document/view-document-page.component';
import { Input1CallbackComponent } from '@Components/input1/input1-callback.component';

const appRoutes: Routes = [

    {
        path: '',
        resolve: {
            theme: Resolvers.WhiteLabelThemeResolver
        },
        canActivate: [Guards.FeatureToggleGuard],
        children: [
            {
                path: 'auth0login', // Just in case bookmarks.
                component: Auth0LoginComponent,
                canActivate: [Guards.AuthV2GuardService]
            },
            { path: 'signature-complete', component: SignatureCallbackComponent },
            { path: 'trust-commerce-complete', component: TrustCommerceCallbackComponent },
            { path: 'input1-complete', component: Input1CallbackComponent },
            { path: 'end-of-application', component: ApplicantEndOfInterviewComponent },
            { path: 'mras/interview', component: InterviewBootstrapComponent},
            {
                path: 'purl',
                loadChildren: () => import('./interview/purl/purl.module').then(m => m.PurlModule)
            },
            {
                path: 'purl/:id',
                loadChildren: () => import('./interview/purl/purl-verification/purl-verification.module').then(m => m.PurlVerificationModule)
            },
            {
                path: 'purlv2/:id',
                loadChildren: () => import('./interview/purl/purl-verification-v2/purl-verification-v2.module').then(m => m.PurlVerificationV2Module)
            },
            {
                path: 'login',
                component: Auth0LoginComponent,
                canActivate: [Guards.AuthV2GuardService],
            },
            {
                path: '',
                canActivate: [Guards.AuthGuardService],
                canActivateChild: [Guards.ValidThemeGuard, Guards.RoleGuard, Guards.RedirectGuard],
                component: NavigationComponent,
                children: [

                    { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
                    AuditRoutes,
                    { path: 'assign-and-restart', component: CaseAssignmentAndRestartComponent },
                    {
                        path: 'dashboard',
                        loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule)
                    },
                    {
                        path: 'mydashboard',
                        component: MyDashboardComponent,
                        canActivate: [Guards.AclGuardService],
                        data: {
                            permission: 'Dashboard',
                            permissionType: 'V',
                            rolesNotAllowed: ['Applicant']
                        },
                        resolve: {
                            accounts: Resolvers.AccountsResolverService
                        }
                    },
                    {
                        path: 'mycases',
                        component: MyCasesComponent,
                        canActivate: [Guards.AclGuardService],
                        data: {
                            permission: 'Case',
                            permissionType: 'R',
                            rolesNotAllowed: ['Applicant']
                        },
                        resolve: {
                            accounts: Resolvers.AccountsResolverService
                        }
                    },{
                        path: 'myaccount',
                        component: MyAccountComponent,
                        canActivate: [Guards.AclGuardService],
                        data: {
                        permission: 'My Account',
                        permissionType: 'V'
                        }
                    },
                    CaseManagerRoute,
                    {
                        path: 'clients', component: ActiveAccountsComponent,
                        data: {
                            isConfigView: true,
                        }
                    },
                    { path: 'roles', component: RoleBuilderComponent,
                        canActivate: [Guards.AclGuardService],
                        data: {
                        permission: 'Roles',
                        permissionType: 'W'
                        }
                     },
                    {
                        path: 'accounts', component: ActiveAccountsComponent,
                        data: {
                            isConfigView: false,
                        }
                    },
        
                    {
                        path: 'teams',
                        component: AppTeamsComponent,
                        canActivate: [Guards.AclGuardService],
                        data: {
                        permission: 'View Teams',
                        permissionType: 'V'
                        }
                    },
                    {
                        path: 'team/:teamId/overview',
                        component: TeamOverviewComponent,
                        canActivate: [Guards.AclGuardService],
                        data: {
                        permission: 'View Teams',
                        permissionType: 'V'
                        }
                        },
                    {
                        path: 'reports',
                        canActivate: [Guards.AclGuardService],
                        data: {
                            permission: 'Reports',
                            permissionType: 'V',
                        },
                        loadChildren: () => import('./reports/reports.module').then(m => m.ReportsModule)
                    },
                    {
                        path: 'callmanager',
                        canActivate: [Guards.AclGuardService],
                        data: {
                            permission: 'CallManager',
                            permissionType: 'V'
                        },
                        loadChildren: () => import('./call-manager/call-manager.module').then(m => m.CallManagerModule)
                    },
                    {
                        path: 'navigation/endofcall/:id/:tenant',
                        loadChildren: () => import('./endofcall/endofcall.module').then(m => m.EndOfCallModule)
                    },
                    { path: 'navigation/dashboard', redirectTo: 'dashboard' },
                    {
                        path: 'interview', // resolve client and account from query string
                        component: InterviewComponent,
                        resolve: {
                            account: Resolvers.ActiveAccountResolverService
                        }
                    },
                    {
                        path: 'manage-users',
                        loadChildren: () => import('./manage-users/manage-users.module').then(m => m.ManageUsersModule)
                    },
                    {
                        path: 'system',
                        loadChildren: () => import('./system/system.module').then(m => m.SystemModule)
                    },
                    {
                        path: 'client/:clientCode',
                        canActivate: [Guards.ClientGuardService],
                        resolve: {
                            client: Resolvers.ClientResolverService
                        },
                        children: [
                            { path: '', redirectTo: 'details', pathMatch: 'full' },
                            { path: 'details', component: ClientDetailsComponent },
                            {
                                path: 'account/new',
                                component: AccountConfigComponent,
                                data: {
                                    new: true
                                }
                            },
                            {
                                path: 'account',
                                children: [
                                    { path: 'new', component: AccountConfigComponent, data: { new: true } },
                                    {
                                        path: ':accountCode',
                                        children: [
                                            {
                                                path: ':versionNumber',
                                                resolve: {
                                                    account: Resolvers.AccountResolverService
                                                },
                                                children: [
                                                    // {
                                                    //     path: 'settings/:settingType/:subTab',
                                                    //     component: AccountConfigComponent,
                                                    //     data: {
                                                    //         new: false,
                                                    //         activeTab: 'Settings'
                                                    //     },
                                                    //     canDeactivate: [CanNavigateAwayWithConfirmGuardAccountConfig]
                                                    // },
                                                    // {
                                                    //     path: 'settings/:settingType',
                                                    //     component: AccountConfigComponent,
                                                    //     data: {
                                                    //         new: false,
                                                    //         activeTab: 'Settings'
                                                    //     },
                                                    //     canDeactivate: [CanNavigateAwayWithConfirmGuardAccountConfig]
                                                    // },
                                                    {
                                                        path: 'configuration/:tabName',
                                                        component: AccountConfigComponent,
                                                        data: {
                                                            new: false
                                                        },
                                                        canDeactivate: [CanNavigateAwayWithConfirmGuardAccountConfig]
                                                    },
                                                    {
                                                        path: '',
                                                        component: AccountConfigComponent,
                                                        pathMatch: 'full',
                                                        data: {
                                                            new: false
                                                        },
                                                        canDeactivate: [CanNavigateAwayWithConfirmGuardAccountConfig]
                                                    },
                                                    {
                                                        path: 'interview',
                                                        loadChildren: () => import('./interview/interview.module').then(m => m.InterviewModule),
                                                    },
                                                    {
                                                        path: ':caseId',
                                                        canActivate: [Guards.ValidCaseGuard, Guards.AccountSettingGuard],
                                                        data: {
                                                            requiredSetting: 'caseSummaryVersion2',
                                                            isEnabled: false,
                                                            routeRedirect: RoutesEnum.caseSummaryV2
                                                        },
                                                        loadChildren: () => import('./case-summary/case-summary.module').then(m => m.CaseSummaryModule)
                                                    },
                                                    {
                                                        path: ':caseId',
                                                        canActivate: [Guards.ValidCaseGuard, Guards.AccountSettingGuard],
                                                        data: {
                                                            requiredSetting: 'caseSummaryVersion2',
                                                            isEnabled: true,
                                                            routeRedirect: RoutesEnum.caseSummary
                                                        },
                                                        loadChildren: () => import('./case-summary/case-summary-v2.module').then(m => m.CaseSummaryV2Module)
                                                    },
                                                    {
                                                       path: ':caseId/view-document/:caseDocumentId',
                                                       component: ViewDocumentPage,    
                                                        canActivate: [Guards.AclGuardService],
                                                        data: {
                                                            permission: 'Download Documents and Attachments',
                                                            permissionType: 'V',
                                                            rolesNotAllowed: ['Applicant']
                                                        },
                                                    }
                                                ]
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    },
                    { path: '404', component: NotFoundComponent },
                    { path: '**', component: NotFoundComponent }
                ]
            }
        ]
    }
];

@NgModule({
    declarations: [],
    imports: [
        CommonModule,
        RouterModule.forRoot(appRoutes, { initialNavigation: 'disabled' }),
    ]
})
export class AppRoutingModule { }
